import httpFetch from '@/config/httpFetch';
import logger from '@/config/logger';
import { REVALIDATE_TIME } from '@/constants';
import { structureProductsData } from './wizzyHelper';
import { storage } from '@/config';
import { isBrowser } from './helper';

interface SortPropTypes {
  field: string;
  order: string;
  isForParent?: boolean;
  isForVariation?: boolean;
}

interface FacetsPropTypes {
  key:
    | 'all'
    | 'categories'
    | 'brands'
    | 'sellingPrice'
    | 'genders'
    | 'colors'
    | 'sizes'
    | 'avgRatings'
    | 'discountPercentage'
    | 'inStock'
    | 'attributes'
    | 'categories'
    | 'lifestyle'
    | 'texture'
    | 'flavour';
  label?: string;
  order?: number;
  position?: 'left' | 'right';
  buckets?: number[];
  config?: {
    interval: number;
  };
  inventorySources?: {
    key?: string;
    inStock?: any[];
  };
}

interface WizzyAutoCompleteApiPropTypes {
  q: string;
  productsCount?: number;
  minQueryLength?: number;
  minLastWordLength?: number;
  includeOutOfStock?: 'true' | 'false';
  getAllVariants?: 'true' | 'false';
  showOOSProductsInOrder?: 'true' | 'false';
  currency?: string;
  sort?: SortPropTypes[] | string;
  swatch?: any;
  facets?: FacetsPropTypes | string;
  attributeFacetValuesLimit?: number;
  includeSwatchInAttributeFacets?: string[];
}

const WIZZY_BASE_URL = process.env.WIZZY_API_URL;

const WIZZY_FILTER_URL = WIZZY_BASE_URL + '/products/filter';
const WIZZY_SEARCH_URL = WIZZY_BASE_URL + '/products/search';
const WIZZY_VARIATION_URL = WIZZY_BASE_URL + '/products/variation';
const WIZZY_TRENDING_SEARCHES_URL = WIZZY_BASE_URL + '/trendingSearches';
const WIZZY_AUTOCOMPLETE_URL = WIZZY_BASE_URL + '/autocomplete';

const getWizzyRequestOptions = () => {
  let storeId: any = process.env.WIZZY_STORE_ID;
  let apiKey: any = process.env.WIZZY_API_KEY;
  if (storage.get('wizzy_store_id')) {
    storeId = storage.get('wizzy_store_id');
  }
  if (storage.get('wizzy_api_key')) {
    apiKey = storage.get('wizzy_api_key');
  }
  const headers: any = {
    'Content-Type': 'application/json',
    'x-store-id': storeId,
    'x-api-key': apiKey,
  };
  if (isBrowser) {
    if (sessionStorage?.getItem('wizzy_sessionId')) {
      headers['x-wizzy-sessionId'] = sessionStorage.getItem('wizzy_sessionId');
    }
    if (storage?.get('wizzy_userId')) {
      headers['x-wizzy-userId'] = storage.get('wizzy_userId');
    }
  }
  return {
    headers,
  };
};

export const wizzyFilter: any = async (filters: any, options?: any) => {
  // If the query is empty or undefined, return empty array
  const url = WIZZY_FILTER_URL;
  const body: any = {
    filters: JSON.stringify(filters),
  };
  const getCachedResponse = options?.useNextCache;
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.post(url, body, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
      next: {
        revalidate: getCachedResponse ? REVALIDATE_TIME : 0,
      },
    });
    if (!data || data?.result?.length === 0) {
      return { noProductsFound: true };
    }
    if (isBrowser) {
      sessionStorage.setItem('wizzyResponseId', data?.responseId || '');
    }
    return data;
  } catch (e) {
    logger.log('Error on wizzy filter api', e);
    return null;
  }
};

export const wizzySearchApi = async (
  body: WizzyAutoCompleteApiPropTypes,
  options?: any
) => {
  const url = WIZZY_SEARCH_URL;
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.post(url, body, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
    });
    if (!data || data?.result?.length === 0) {
      return { noProductsFound: true };
    }
    if (isBrowser) {
      sessionStorage.setItem('wizzyResponseId', data?.responseId || '');
    }
    return data;
  } catch (e) {
    logger.log('Error on wizzy filter api', e);
    return null;
  }
};

export const wizzyGetVariantDataApi = async (reqBody: any, options?: any) => {
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.post(WIZZY_VARIATION_URL, reqBody, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
    });
    if (!data || data?.result?.length === 0) {
      return { noProductsFound: true };
    }
    const structuredVariantData = structureProductsData([
      data?.payload?.result,
    ]);
    return structuredVariantData[0];
  } catch (e) {
    logger.log('Error on wizzy variation api', e);
    return null;
  }
};

export const wizzyAutoCompleteApi = async (body: any, options?: any) => {
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.post(WIZZY_AUTOCOMPLETE_URL, body, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
    });
    if (data?.payload) {
      if (isBrowser) {
        sessionStorage.setItem('wizzyResponseId', data?.responseId || '');
      }
      return { payload: data?.payload, responseId: data?.responseId };
    }
    return { queries: [] };
  } catch (e) {
    logger.log('Error on wizzy variation api', e);
    return null;
  }
};

export const wizzyTrendingSearchesApi = async (options?: any) => {
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.get(WIZZY_TRENDING_SEARCHES_URL, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
    });
    if (data?.payload) {
      return data?.payload;
    }
    return { queries: [] };
  } catch (e) {
    logger.log('Error on wizzy variation api', e);
    return null;
  }
};

export const getProductsByIds = async (productIds: any[], options?: any) => {
  const url = `${WIZZY_BASE_URL}/products/byIds`;
  let ids = productIds;
  if (!options?.isFavouritesData) {
    ids = productIds?.map(id => ({ id }));
  }
  const requestBody: any = {
    ids: JSON.stringify(ids),
    swatch: JSON.stringify([{ key: 'sizes' }]),
  };
  const { headers } = getWizzyRequestOptions();
  try {
    const { data } = await httpFetch.post(url, requestBody, {
      headers: {
        ...headers,
        ...(options?.headers || {}),
      },
      next: {
        revalidate: REVALIDATE_TIME,
      },
    });
    if (options?.isFavouritesData && Array.isArray(data?.payload?.result)) {
      data.payload.result = data.payload.result.map((item: any) => ({
        ...item,
        isFavouritesProduct: true,
      }));
    }
    const products = structureProductsData(data?.payload?.result) || [];
    return products;
  } catch (e) {
    logger.log('Error on get products by ids api', e);
    return [];
  }
};
